import './order.css';

import { Util } from "../../../utils/util";
import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { Data } from '../../../models/data';
import { ProfileService } from "../../../services/profile-service";
import { RecentOrders } from '../../shared/RecentOrder/recent-orders';
import { Modals, Pages } from '../../../types/enums';
import { OrderService } from '../../../services/order-service';
import { Page } from '../page';
import { DataLayerService } from '../../../services/data-layer-service';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { LoaderSpinner } from '../../shared/LoaderSpinner/loader-spinner';
import { DialogCreators } from '../../../utils/dialog-creators';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { Router } from '../../../core/router';

interface IRecentOrderProps {
    divId?: string;
    action: () => void;
}

export class Orders extends Page {
    private readonly _idRecentOrders = "div_recent_orders_main";
    private readonly _profileService: ProfileService;
    private readonly _orderService: OrderService;
    private readonly _dataLayerService: DataLayerService;
    private readonly _recentOrders: RecentOrders;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, orderService: OrderService, dataLayerService: DataLayerService) {
        super("page_orders", eventBus, data, context, localizationService);
        this._profileService = profileService;
        this._orderService = orderService;
        this._dataLayerService = dataLayerService;
        this._recentOrders = new RecentOrders(this._eventBus, this._data, this._context, this._localizationService, this._profileService, this._orderService, this._dataLayerService);
        this._eventBusListeners();
    }

    public override show(): void {
        super.show();

        if (!this._profileService.user.isLoggedIn()) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING)
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN);
        }

        this._recentOrders.buildRecentlyOrderedItemsSection(this._idRecentOrders);

        this._context.setProperty("upsellSelected", false);
        if (this._dataLayerService)
            this._dataLayerService.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);
    }

    public async initAsync(): Promise<void> { }

    private readonly _eventBusListeners = () => {
        const debouncedBuildRecentOrderSection = Util.Debounce(() => this._recentOrders.buildRecentlyOrderedItemsSection(this._idRecentOrders), 1000, true);
        this._eventBus.on(EventTypes.LOGGED_IN, debouncedBuildRecentOrderSection);
        this._eventBus.on(EventTypes.LOGGED_OUT, this._redirectToHome);
        this._eventBus.on(EventTypes.DATA_RETRIEVED, debouncedBuildRecentOrderSection);
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, () => {
            if (Router.path !== Pages.ORDERS)
                return;

            this._recentOrders.buildRecentlyOrderedItemsSection(this._idRecentOrders);
        });
        this._eventBus.on(EventTypes.LOCATION_CHANGE, (page: Pages, options?: IRecentOrderProps) => {
            if (page !== Pages.ORDERS)
                return;

            if (options) {
                if (options.divId) {
                    Util.scrollToElementId(options.divId, 30, 50, 0, "smooth");
                }

                if (options.action) {
                    this.executeWithRetry(options.action);
                }
            }
        });
        this._eventBus.on(EventTypes.ORDER_COMPLETED, async () => {
            this._profileService.user.setProperty("RecentOrders", null);
            await this._recentOrders.buildRecentlyOrderedItemsSection(this._idRecentOrders);
        });
    }

    private readonly executeWithRetry = (action: () => void, retries: number = 14, delayMs: number = 150): void => {
        const loader = new LoaderSpinner();

        const attempt = (remainingRetries: number) => {
            if (this._data.getProperty("Items")?.length > 0) {
                action();
                loader?.destroy();
            } else if (remainingRetries > 0) {
                setTimeout(() => attempt(remainingRetries - 1), delayMs);
            } else {
                loader?.destroy();
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayerService,
                    this._localizationService.names("Error_CustomizeUnable"),
                    true
                );
            }
        };

        attempt(retries);
    };

    private readonly _redirectToHome = () => {
        if (Router.path === Pages.ORDERS)
            this._redirectAfterLogout();
    }
}